import { useState, useCallback, useEffect } from "react";
import { Link } from "react-router";
import { IconButton, Stack } from "@mui/material";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";

import classes from "./Home.module.css";
import portrait from "../../Assets/portrait.jpeg";
import Room from "../../Assets/Room.jpeg";
import { reviews } from "../Reviews/clientReviews";
import {
  BOOKING_URL,
  trackBookingClick,
  trackContactClick,
  trackTreatmentCardClick,
  trackFaqToggle,
  trackCarouselInteraction,
} from "../../utils/tracking";
import useSectionView from "../../utils/useSectionView";

const trustPoints = [
  {
    icon: "🏥",
    title: "Nurse-Led",
    text: "Qualified nurse since 2019 with advanced aesthetic training",
  },
  {
    icon: "✨",
    title: "Results-Driven",
    text: "Personalised treatment plans for visible, natural results",
  },
  {
    icon: "🛡️",
    title: "Safe & Clinical",
    text: "Medical-grade products and rigorous safety protocols",
  },
  {
    icon: "💬",
    title: "Free Consultation",
    text: "Every journey begins with a complimentary skin assessment",
  },
];

const treatments = [
  {
    icon: "💎",
    title: "Advanced Facials",
    price: "From £65",
    description:
      "Bespoke facials addressing hydration, congestion, pigmentation and ageing concerns",
    to: "/treatments/advanced-facials",
  },
  {
    icon: "🪡",
    title: "Microneedling",
    price: "From £120",
    description:
      "Medical-grade collagen induction therapy for scarring, texture and rejuvenation",
    to: "/treatments/microneedling",
  },
  {
    icon: "🧪",
    title: "Chemical Peels",
    price: "From £80",
    description:
      "Professional peels targeting pigmentation, acne, fine lines and dull complexion",
    to: "/treatments/chemical-peels",
  },
  {
    icon: "💡",
    title: "Dermalux LED",
    price: "From £35",
    description:
      "LED light therapy to calm, heal and rejuvenate the skin at a cellular level",
    to: "/treatments/advanced-facials",
  },
  {
    icon: "💧",
    title: "Skin Boosters",
    price: "From £100",
    description:
      "Injectable hyaluronic acid treatments for deep hydration and plumpness",
    to: "/treatments/skin-boosters",
  },
  {
    icon: "💉",
    title: "Vitamin Injections",
    price: "From £25",
    description:
      "B12 and biotin injections for energy, immunity and overall wellbeing",
    to: "/treatments/vitamin-injections",
  },
];

const faqs = [
  {
    q: "Do I need a consultation before booking a treatment?",
    a: "Every new client begins with a complimentary consultation. This allows us to understand your skin concerns, discuss your goals, and create a personalised treatment plan. Your first treatment can be carried out on the same day.",
  },
  {
    q: "Is microneedling painful?",
    a: "Most clients describe it as a mild tingling sensation. A topical numbing cream is applied beforehand to ensure your comfort. Any redness typically subsides within 24–48 hours.",
  },
  {
    q: "How many sessions will I need to see results?",
    a: "This depends on your individual skin concerns and goals. Many clients notice immediate improvements after a single session. For optimal results, a course of treatments is usually recommended, which we'll discuss during your consultation.",
  },
  {
    q: "What skincare products do you use?",
    a: "We use professional-grade products including Medik8 — a clinically proven skincare brand based on science and results. We can also advise on an at-home skincare routine to complement your in-clinic treatments.",
  },
  {
    q: "Where are you based and what are your opening hours?",
    a: "Radiance Aesthetics is located at Laurel House, Atherton Road, Hindley, Wigan, WN2 3XD — directly above Yoga Universe, and easily accessible from Wigan town centre, Leigh, Atherton, Tyldesley, and surrounding areas. We're open Monday to Saturday, 9am to 5pm. Our clinic provides a calm, private, and luxurious treatment environment.",
  },
];

const Home = () => {
  const [reviewIndex, setReviewIndex] = useState(0);
  const [expandedFaq, setExpandedFaq] = useState(null);
  const reviewsSectionRef = useSectionView("Home Reviews");
  const faqSectionRef = useSectionView("Home FAQ");

  useEffect(() => {
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqs.map((faq) => ({
        "@type": "Question",
        name: faq.q,
        acceptedAnswer: { "@type": "Answer", text: faq.a },
      })),
    });
    document.head.appendChild(script);
    return () => document.head.removeChild(script);
  }, []);

  const nextReview = useCallback(() => {
    trackCarouselInteraction("Home Reviews", "next");
    setReviewIndex((i) => (i === reviews.length - 1 ? 0 : i + 1));
  }, []);

  const prevReview = useCallback(() => {
    trackCarouselInteraction("Home Reviews", "prev");
    setReviewIndex((i) => (i === 0 ? reviews.length - 1 : i - 1));
  }, []);

  const currentReview = reviews[reviewIndex];

  return (
    <>
      {/* Hero Section */}
      <section className={classes.heroSection}>
        <div className={classes.heroContainer}>
          <div className={classes.heroContent}>
            <p className={classes.heroSubtitle}>Nurse-Led Aesthetics Clinic</p>
            <h1 className={classes.heroTitle}>
              Your Skin Deserves Expert Care
            </h1>
            <p className={classes.heroDescription}>
              Advanced facial treatments and skin rejuvenation in Hindley,
              Wigan. Personalised care from a qualified nurse — because
              beautiful skin starts with clinical expertise.
            </p>
            <a
              href={BOOKING_URL}
              className={classes.heroCta}
              target="_blank"
              rel="noreferrer"
              onClick={() => trackBookingClick("Home Hero")}
            >
              Book Your Consultation
            </a>
          </div>
          <div className={classes.heroImageWrapper}>
            <img
              className={classes.heroImage}
              src={portrait}
              alt="Elisha, qualified nurse and skin specialist at Radiance Aesthetics Wigan"
              loading="eager"
            />
          </div>
        </div>
      </section>

      {/* Trust Signals */}
      <section
        className={classes.trustSection}
        aria-label="Why choose Radiance Aesthetics"
      >
        <div className={classes.trustContainer}>
          <div className={classes.trustGrid}>
            {trustPoints.map((point) => (
              <div className={classes.trustItem} key={point.title}>
                <div className={classes.trustIcon} aria-hidden="true">
                  {point.icon}
                </div>
                <h3 className={classes.trustTitle}>{point.title}</h3>
                <p className={classes.trustText}>{point.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Strip */}
      <section
        className={classes.contactStrip}
        aria-label="Contact information"
      >
        <div className={classes.contactStripContainer}>
          <div className={classes.contactStripItem}>
            <span className={classes.contactStripIcon} aria-hidden="true">
              📍
            </span>
            <span>
              Laurel House, Atherton Road, Hindley, Wigan, WN2 3XD (above
              Yoga Universe)
            </span>
          </div>
          <div className={classes.contactStripItem}>
            <span className={classes.contactStripIcon} aria-hidden="true">
              📞
            </span>
            <a
              href="tel:07555379004"
              onClick={() => trackContactClick("phone", "Home Contact Strip")}
            >
              07555 379004
            </a>
          </div>
          <div className={classes.contactStripItem}>
            <span className={classes.contactStripIcon} aria-hidden="true">
              🕐
            </span>
            <span>Mon – Sat: 9am – 5pm</span>
          </div>
        </div>
      </section>

      {/* Treatments Overview */}
      <section
        className={classes.treatmentsSection}
        aria-label="Our treatments"
      >
        <div className={classes.treatmentsContainer}>
          <p className={classes.sectionLabel}>Treatments</p>
          <h2 className={classes.sectionTitle}>Tailored Skin Solutions</h2>
          <p className={classes.sectionDescription}>
            Every treatment begins with understanding your unique skin. From
            advanced facials to medical-grade rejuvenation, each session is
            personalised to deliver visible, lasting results.
          </p>
          <div className={classes.treatmentsGrid}>
            {treatments.map((t) => (
              <Link
                to={t.to}
                className={classes.treatmentCard}
                key={t.title}
                onClick={() =>
                  trackTreatmentCardClick(t.title, t.to, "Home Treatments Grid")
                }
              >
                <div className={classes.treatmentCardIcon} aria-hidden="true">
                  {t.icon}
                </div>
                <h3 className={classes.treatmentCardTitle}>{t.title}</h3>
                <p className={classes.treatmentCardPrice}>{t.price}</p>
                <p className={classes.treatmentCardDescription}>
                  {t.description}
                </p>
              </Link>
            ))}
          </div>
          <Link
            to="/treatments"
            className={classes.viewAllLink}
            onClick={() =>
              trackTreatmentCardClick(
                "View All Treatments",
                "/treatments",
                "Home Treatments Grid",
              )
            }
          >
            View All Treatments →
          </Link>
        </div>
      </section>

      {/* About Section */}
      <section className={classes.aboutSection} aria-label="About Elisha">
        <div className={classes.aboutContainer}>
          <div className={classes.aboutImageWrapper}>
            <img
              className={classes.aboutImage}
              src={Room}
              alt="Radiance Aesthetics treatment room in Hindley, Wigan"
              loading="lazy"
            />
          </div>
          <div className={classes.aboutContent}>
            <p className={classes.sectionLabel}>Your Practitioner</p>
            <h2 className={classes.sectionTitle} style={{ textAlign: "left" }}>
              Meet Elisha
            </h2>
            <p className={classes.aboutText}>
              A qualified nurse since 2019, Elisha brings clinical expertise and
              a genuine passion for skin health to every treatment. With
              specialist training across advanced aesthetics, she combines
              medical knowledge with an artist's eye for natural results.
            </p>
            <p className={classes.aboutText}>
              Every appointment begins with listening — understanding your
              concerns, your lifestyle, and your goals. Whether you're seeking
              to address a specific skin condition or simply wanting to invest
              in your skin's long-term health, you're in safe, expert hands.
            </p>
            <a
              href={BOOKING_URL}
              className={classes.aboutCta}
              target="_blank"
              rel="noreferrer"
              onClick={() => trackBookingClick("Home About")}
            >
              Start Your Skin Journey
            </a>
          </div>
        </div>
      </section>

      {/* Reviews Section */}
      <section
        className={classes.reviewsSection}
        aria-label="Client reviews"
        ref={reviewsSectionRef}
      >
        <div className={classes.reviewsContainer}>
          <p className={classes.sectionLabel}>Testimonials</p>
          <h2 className={classes.sectionTitle}>Trusted by Our Clients</h2>
          <p className={classes.sectionDescription}>
            Every review is from a real client. Read why people keep coming back
            to Radiance Aesthetics.
          </p>
          <div className={classes.reviewCard}>
            <div
              className={classes.reviewStars}
              aria-label={`${currentReview.rating} out of 5 stars`}
            >
              {"★".repeat(currentReview.rating)}
            </div>
            <p className={classes.reviewText}>"{currentReview.text}"</p>
            <p className={classes.reviewAuthor}>{currentReview.name}</p>
            <p className={classes.reviewService}>{currentReview.service}</p>
          </div>
          <Stack
            direction="row"
            justifyContent="center"
            alignItems="center"
            gap={2}
            sx={{ mt: 3 }}
          >
            <IconButton
              onClick={prevReview}
              aria-label="Previous review"
              sx={{
                backgroundColor: "white",
                boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                "&:hover": { backgroundColor: "#f9f9f9" },
              }}
            >
              <KeyboardArrowLeftIcon />
            </IconButton>
            <span style={{ fontSize: "14px", color: "#8a7d77" }}>
              {reviewIndex + 1} / {reviews.length}
            </span>
            <IconButton
              onClick={nextReview}
              aria-label="Next review"
              sx={{
                backgroundColor: "white",
                boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                "&:hover": { backgroundColor: "#f9f9f9" },
              }}
            >
              <KeyboardArrowRightIcon />
            </IconButton>
          </Stack>
        </div>
      </section>

      {/* FAQ Section */}
      <section
        className={classes.faqSection}
        aria-label="Frequently asked questions"
        ref={faqSectionRef}
      >
        <div className={classes.faqContainer}>
          <p className={classes.sectionLabel}>FAQ</p>
          <h2 className={classes.sectionTitle}>Common Questions</h2>
          <p className={classes.sectionDescription}>
            Everything you need to know before your first visit.
          </p>
          {faqs.map((faq, i) => (
            <div className={classes.faqItem} key={i}>
              <h3
                className={classes.faqQuestion}
                onClick={() => {
                  const opening = expandedFaq !== i;
                  trackFaqToggle(faq.q, i, opening);
                  setExpandedFaq(opening ? i : null);
                }}
                role="button"
                aria-expanded={expandedFaq === i}
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    const opening = expandedFaq !== i;
                    trackFaqToggle(faq.q, i, opening);
                    setExpandedFaq(opening ? i : null);
                  }
                }}
              >
                {faq.q} {expandedFaq === i ? "−" : "+"}
              </h3>
              {expandedFaq === i && (
                <p className={classes.faqAnswer}>{faq.a}</p>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section className={classes.ctaSection} aria-label="Book appointment">
        <div className={classes.ctaContainer}>
          <h2 className={classes.ctaTitle} style={{ color: "white" }}>
            Ready to Transform Your Skin?
          </h2>
          <p className={classes.ctaDescription}>
            Book your complimentary consultation today. Discover a personalised
            treatment plan designed around your unique skin goals.
          </p>
          <a
            href={BOOKING_URL}
            className={classes.ctaButton}
            target="_blank"
            rel="noreferrer"
            onClick={() => trackBookingClick("Home Bottom CTA")}
          >
            Book Your Consultation
          </a>
        </div>
      </section>
    </>
  );
};

export default Home;
